<template>
  <span class="pi-def" :class="{ 'is-open': open }" @keydown.esc="onEscape" @mouseleave="hover = false">
    <button ref="button" type="button" class="pi-def__btn"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="panelId"
            :aria-label="'Definição de ' + title"
            @click="pinned = !pinned"
            @mouseenter="hover = true"
            @focus="focused = true"
            @blur="focused = false">?</button>
    <span v-show="open" ref="panel" :id="panelId" class="pi-def__panel" :style="panelStyle" role="tooltip">
      <strong class="pi-def__name">{{ title }}</strong>
      <template v-if="panel">
        <span v-for="block in panel.blocks" :key="block.label" class="pi-def__block">
          <span class="pi-def__label">{{ block.label }}</span>
          {{ block.text }}
        </span>
        <span v-if="panel.formula" class="pi-def__line"><span class="pi-def__label">Fórmula:</span> {{ panel.formula }}</span>
        <span v-if="panel.caveat" class="pi-def__line"><span class="pi-def__label">{{ caveatLabel }}:</span> {{ panel.caveat }}</span>
        <span v-if="panel.seeAlso.length" class="pi-def__line">
          <span class="pi-def__label">{{ seeAlsoLabel }}:</span> {{ panel.seeAlso.join(', ') }}
        </span>
        <span v-if="panel.versionText" class="pi-def__version">{{ panel.versionText }}</span>
      </template>
      <template v-else>
        <span v-if="description" class="pi-def__about">{{ description }}</span>
        <span v-for="line in lines" :key="line.label" class="pi-def__line">
          <span class="pi-def__label">{{ line.label }}:</span> {{ line.text }}
        </span>
        <span v-if="!definition" class="pi-def__line">Definição ainda não catalogada.</span>
        <span v-if="versionText" class="pi-def__version">{{ versionText }}</span>
      </template>
    </span>
  </span>
</template>

<script>
import { definitionDescription, definitionFor, definitionLines, definitionVersionText } from "./piDefinitions.js";
import { CAVEAT_LABEL, SEE_ALSO_LABEL, glossaryEntry, glossaryPanel } from "./glossary/index.js";
import { escapeClosesPanel, popoverPosition } from "./piPopover.js";

let sequence = 0;

export default {
  name: "pi-definition-tooltip",
  props: {
    definitionKey: { type: String, default: "" },
    infoKey: { type: String, default: "" },
    entry: { type: Object, default: null },
    label: { type: String, default: "" },
    version: { type: Number, default: null },
    overrides: { type: Array, default: () => [] },
    align: { type: String, default: "left" },
  },
  data() {
    sequence += 1;
    return {
      panelId: "pi-def-" + sequence,
      pinned: false,
      hover: false,
      focused: false,
      placed: false,
      panelLeft: 0,
      panelTop: 0,
      caveatLabel: CAVEAT_LABEL,
      seeAlsoLabel: SEE_ALSO_LABEL,
    };
  },
  computed: {
    glossary() {
      if (this.entry && typeof this.entry === "object") return this.entry;
      return glossaryEntry(this.infoKey);
    },
    panel() {
      return glossaryPanel(this.glossary, { overrides: this.overrides, version: this.version, label: this.label });
    },
    definition() {
      return definitionFor(this.definitionKey, this.overrides);
    },
    title() {
      if (this.panel) return this.panel.title;
      return (this.definition && this.definition.name) || this.label || "indicador";
    },
    description() {
      return definitionDescription(this.definition);
    },
    lines() {
      return definitionLines(this.definition);
    },
    versionText() {
      return definitionVersionText(this.definition, this.version);
    },
    open() {
      return this.pinned || this.hover || this.focused;
    },
    panelStyle() {
      return {
        left: this.panelLeft + "px",
        top: this.panelTop + "px",
        visibility: this.placed ? "visible" : "hidden",
      };
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.listen();
        this.$nextTick(this.place);
      } else {
        this.unlisten();
        this.placed = false;
      }
    },
  },
  beforeDestroy() {
    this.unlisten();
  },
  methods: {
    close() {
      this.pinned = false;
      this.hover = false;
      this.focused = false;
    },
    onEscape(event) {
      if (escapeClosesPanel(this.open, event)) this.close();
    },
    place() {
      const button = this.$refs.button;
      const panel = this.$refs.panel;
      if (!this.open || !button || !panel) return;
      const anchor = button.getBoundingClientRect();
      const position = popoverPosition({
        align: this.align,
        anchorLeft: anchor.left,
        anchorRight: anchor.right,
        anchorTop: anchor.top,
        anchorBottom: anchor.bottom,
        panelWidth: panel.offsetWidth,
        panelHeight: panel.offsetHeight,
        viewportWidth: document.documentElement.clientWidth,
        viewportHeight: window.innerHeight,
      });
      this.panelLeft = position.left;
      this.panelTop = position.top;
      this.placed = true;
    },
    listen() {
      if (this.listening) return;
      this.listening = true;
      window.addEventListener("scroll", this.place, true);
      window.addEventListener("resize", this.place);
    },
    unlisten() {
      if (!this.listening) return;
      this.listening = false;
      window.removeEventListener("scroll", this.place, true);
      window.removeEventListener("resize", this.place);
    },
  },
};
</script>

<style scoped>
.pi-def {
  position: relative;
  display: inline-block;
  z-index: 2;
}

.pi-def.is-open {
  z-index: 1060;
}

.pi-def__btn {
  width: 18px;
  height: 18px;
  padding: 0;
  border: 1px solid #c5cad3;
  border-radius: 50%;
  background: #ffffff;
  color: #4e5560;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  cursor: pointer;
}

.pi-def__btn:hover,
.pi-def__btn[aria-expanded="true"] {
  border-color: #1b59f8;
  color: #1b59f8;
}

.pi-def__panel {
  position: fixed;
  z-index: 1060;
  display: block;
  width: 340px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
  overflow-y: auto;
  padding: 10px 12px;
  border: 1px solid #d9dde3;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: #3a424e;
  text-align: left;
  text-transform: none;
  white-space: normal;
}

.pi-def__name {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
}

.pi-def__about {
  display: block;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eceff3;
  line-height: 1.45;
}

.pi-def__line {
  display: block;
  margin-bottom: 4px;
}

.pi-def__block {
  display: block;
  margin-bottom: 8px;
}

.pi-def__block .pi-def__label {
  display: block;
  margin-bottom: 1px;
  color: #1f2933;
}

.pi-def__label {
  font-weight: 600;
}

.pi-def__version {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  color: #6b7280;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piDefinitionTooltip.vue